import { select, t } from '@lingui/core/macro';
import { useTheme } from '@mui/material';
import { useIndexQuery, useShowQuery } from 'api';
import { OfferIconMUI } from 'components/icons/OfferIconMUI';
import RoundedExclamation from 'components/icons/RoundedExclamation';
import { WarningIconMUI2 } from 'components/icons/WarningIconMUI2';
import { NavSection } from 'components/nav-section/NavSection';
import type { NavListProps } from 'components/nav-section/type';
import SvgIcon from 'components/SvgIcon';
import { useCurrentLabOrThrow, useCurrentRoleOrThrow } from 'hooks/slices';
import { useGetResellerWordingType } from 'hooks/useGetResellerWordingType';
import { useRegionalDirectorIdsFilter } from 'hooks/useRegionalDirectorIdsFilter';
import { truthy } from 'utils/arrayUtils';

export function LabNavSection() {
  const theme = useTheme();

  const currentRole = useCurrentRoleOrThrow();
  const isResourceAdmin = currentRole.kind === 'lab_admin';
  const { id: labId, is_client: isClient } = useCurrentLabOrThrow();
  const resellerWording = useGetResellerWordingType();
  const { data: labData } = useIndexQuery('labs/lab/statistics', undefined, {
    enabled: currentRole.kind !== 'lab_sales_rep_no_access',
  });
  const awaitingLabtradeInstancesCount = labData?.statistics.awaiting_lab_trades_instances_count;
  const labNotSeenInvoicesCount = labData?.statistics.not_seen_invoices_count;
  const pendingExpiredProductsRequestsCount =
    labData?.statistics.pending_expired_products_requests_count;

  const { data: currentLab } = useShowQuery('labs', labId);
  const shouldSetupStripe =
    currentLab?.lab.stripe_lab_settings?.is_feature_enabled &&
    currentLab.lab.stripe_lab_settings.payment_method === 'sepa_debit' &&
    currentLab?.lab.stripe_lab_settings?.status !== 'ok';

  const useExpiredLabSettingQuery = useIndexQuery(
    { route: 'expired/shared/labs/:id/config', pathParams: { id: labId } },
    {}
  );
  const expiredLabSetting = useExpiredLabSettingQuery.data?.expired_lab_config;
  const regionalDirectorIds = useRegionalDirectorIdsFilter();

  const navItems: NavListProps[] = [
    {
      title: t`Opérations`,
      icon: <OfferIconMUI />,
      ...(!!awaitingLabtradeInstancesCount && { badgeCount: awaitingLabtradeInstancesCount }),
      parentTo: '/lab/trades' as const,
      children: [
        {
          title: t`Dashboard`,
          to: '/lab/trades/dashboard' as const,
          search: { regionalDirectorIds },
        },
        {
          title: t`Opérations`,
          to: '/lab/trades/master_trades' as const,
        },
        {
          title: t`Validation`,
          to: '/lab/trades/list' as const,
          search: {
            tab: 'awaiting_validation_by_lab' as const,
            regionalDirectorIds,
          },
          ...(!!awaitingLabtradeInstancesCount && { badgeCount: awaitingLabtradeInstancesCount }),
        },
      ],
    },
    {
      title: t`Réclamations`,
      icon: <WarningIconMUI2 />,
      ...(!!labNotSeenInvoicesCount && { badgeCount: labNotSeenInvoicesCount }),
      parentTo: '/lab/claims' as const,
      children: [
        {
          title: t`Dashboard`,
          to: '/lab/claims/dashboard' as const,
          search: { regionalDirectorIds },
          rightIcon: !isClient ? <SvgIcon name="ic_lock" /> : undefined,
        },
        {
          title: t`Suivi`,
          to: '/lab/claims/list' as const,
          search: {
            tab: 'pending' as const,
            regionalDirectorIds,
          },
          ...(!!labNotSeenInvoicesCount && { badgeCount: labNotSeenInvoicesCount }),
        },
      ],
    },
    expiredLabSetting?.is_feature_enabled && {
      title: t`Périmés`,
      icon: <SvgIcon name="ic_expired" />,
      ...(!!pendingExpiredProductsRequestsCount && {
        badgeCount: pendingExpiredProductsRequestsCount,
      }),
      parentTo: '/lab/expired_products_requests' as const,
      children: [
        {
          title: t`Dashboard`,
          to: '/lab/expired_products_requests/dashboard' as const,
          search: { regionalDirectorIds },
        },
        {
          title: t`Suivi`,
          to: '/lab/expired_products_requests/list' as const,
          search: { regionalDirectorIds },
          ...(!!pendingExpiredProductsRequestsCount && {
            badgeCount: pendingExpiredProductsRequestsCount,
          }),
        },
      ],
    },
    {
      title: select(resellerWording, {
        pharmacy: 'Pharmacies',
        point_of_sale: 'Points de vente',
        other: '',
      }),
      icon: <SvgIcon name="ic_pharma" />,
      parentTo: ['/lab/pharmacies' as const, '/lab/pharmacy_lists' as const],
      children: [
        {
          title: select(resellerWording, {
            pharmacy: 'Pharmacies',
            point_of_sale: 'Points de vente',
            other: '',
          }),
          to: '/lab/pharmacies' as const,
          search: { regionalDirectorIds },
        },
        ...(isResourceAdmin
          ? [
              {
                title: select(resellerWording, {
                  pharmacy: 'Liste de pharmacies',
                  point_of_sale: 'Liste de points de vente',
                  other: '',
                }),
                to: '/lab/pharmacy_lists' as const,
              },
            ]
          : []),
      ],
    },
    currentRole.kind !== 'lab_regional_director' && {
      title: t`Groupements`,
      to: '/lab/groups_labs' as const,
      icon: <SvgIcon name="ic_group" />,
    },
    currentRole.kind !== 'lab_regional_director' && {
      title: t`FAQ Labo`,
      to: '/lab/guide_items' as const,
      icon: <SvgIcon name="ic_info" />,
    },
    isResourceAdmin && {
      title: t`Paramètres`,
      to: '/lab/$id/edit' as const,
      params: { id: labId },
      icon: <SvgIcon name="ic_settings" />,
      ...(shouldSetupStripe && {
        rightIcon: <RoundedExclamation size={15} color={theme.palette.warning.main} />,
      }),
    },
    {
      title: 'Aide',
      icon: <SvgIcon name="ic_question" />,
      href: 'https://intercom.help/faks/fr',
    },
  ].filter(truthy);

  return <NavSection navItems={navItems} />;
}
